import {
  EnvironmentProviders,
  signal,
  Injectable,
  InjectionToken,
  computed,
  inject,
  makeEnvironmentProviders,
  PLATFORM_ID
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { providePrimeNG } from 'primeng/config';
import { MeasuredPreset } from '../themes/measured-preset';

// =============================================================================
// CONFIGURATION TYPES
// =============================================================================

/**
 * Configuration options for the Measured Design System
 */
export interface MdsProviderOptions {
  /**
   * CSS variable prefix for all design tokens
   * @default 'm'
   */
  prefix?: string;

  /**
   * CSS selector for dark mode activation
   * Set to false or 'none' to disable dark mode
   * @default '.m-dark'
   */
  darkModeSelector?: string | false;

  /**
   * Enable ripple effect on interactive elements
   * @default false
   */
  ripple?: boolean;

  /**
   * CSS layer configuration for style ordering
   */
  cssLayer?: {
    name: string;
    order: string;
  };
}

// =============================================================================
// INJECTION TOKEN
// =============================================================================

/**
 * Injection token for the resolved dark mode selector.
 * Provided by `provideMeasuredDesignSystem` so that `MdsThemeService`
 * stays in sync with the PrimeNG theme configuration.
 */
export const MDS_DARK_MODE_SELECTOR = new InjectionToken<string | false>(
  'MDS_DARK_MODE_SELECTOR',
  { providedIn: 'root', factory: () => DEFAULT_OPTIONS.darkModeSelector }
);

// =============================================================================
// THEME SERVICE
// =============================================================================

/**
 * Service for managing the Measured Design System theme at runtime.
 *
 * Provides methods to toggle dark mode and check current theme state.
 *
 * @example
 * ```typescript
 * @Component({...})
 * export class MyComponent {
 *   private themeService = inject(MdsThemeService);
 *
 *   toggleTheme() {
 *     this.themeService.toggleDarkMode();
 *   }
 *
 *   get isDark() {
 *     return this.themeService.isDarkMode();
 *   }
 * }
 * ```
 */
@Injectable({ providedIn: 'root' })
export class MdsThemeService {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly darkModeSelector = inject(MDS_DARK_MODE_SELECTOR);
  private readonly _darkMode = signal(false);
  private readonly _initialized = signal(false);

  /** Class name derived from the configured selector (e.g. '.m-dark' → 'm-dark') */
  private readonly darkModeClass: string | null = (() => {
    if (!this.darkModeSelector || this.darkModeSelector === 'none') {
      return null;
    }
    return this.darkModeSelector.replace(/^\./, '');
  })();

  /**
   * Reactive signal indicating if dark mode is currently active
   */
  readonly isDarkMode = computed(() => this._darkMode());

  /**
   * Reactive signal indicating if the theme service has been initialized
   */
  readonly isInitialized = computed(() => this._initialized());

  /**
   * Initialize the theme service.
   * Call this in your app initialization to respect user's system preference.
   *
   * @param respectSystemPreference - If true, initializes based on system color scheme
   */
  initialize(respectSystemPreference = false): void {
    if (!isPlatformBrowser(this.platformId) || this._initialized()) {
      return;
    }

    if (respectSystemPreference) {
      const prefersDark = window.matchMedia(
        '(prefers-color-scheme: dark)'
      ).matches;
      this.setDarkMode(prefersDark);

      // Listen for system preference changes
      window
        .matchMedia('(prefers-color-scheme: dark)')
        .addEventListener('change', (e) => {
          this.setDarkMode(e.matches);
        });
    }

    this._initialized.set(true);
  }

  /**
   * Toggle between light and dark mode
   */
  toggleDarkMode(): void {
    this._darkMode.update((v) => !v);
    this.applyDarkMode(this._darkMode());
  }

  /**
   * Explicitly set dark mode state
   *
   * @param enabled - Whether dark mode should be enabled
   */
  setDarkMode(enabled: boolean): void {
    this._darkMode.set(enabled);
    this.applyDarkMode(enabled);
  }

  /**
   * Apply dark mode class to document
   */
  private applyDarkMode(enabled: boolean): void {
    if (!isPlatformBrowser(this.platformId) || !this.darkModeClass) {
      return;
    }
    document.documentElement.classList.toggle(this.darkModeClass, enabled);
  }
}

// =============================================================================
// PROVIDER FUNCTION
// =============================================================================

/**
 * Default configuration options
 */
const DEFAULT_OPTIONS: Required<MdsProviderOptions> = {
  prefix: 'm',
  darkModeSelector: '.m-dark',
  ripple: false,
  cssLayer: {
    name: 'primeng',
    order: 'tailwind-base, app-base, primeng, tailwind-utilities'
  }
};

/**
 * Provides the Measured Design System configuration for Angular applications.
 *
 * This function should be called in your app.config.ts providers array.
 *
 * @param options - Optional configuration to customize the design system
 * @returns EnvironmentProviders for Angular DI
 *
 * @example
 * ```typescript
 * // app.config.ts
 * import { provideMeasuredDesignSystem } from '@mds/providers';
 *
 * export const appConfig: ApplicationConfig = {
 *   providers: [
 *     provideMeasuredDesignSystem(),
 *     // ... other providers
 *   ],
 * };
 * ```
 *
 * @example
 * ```typescript
 * // With custom options
 * provideMeasuredDesignSystem({
 *   prefix: 'my-app',
 *   darkModeSelector: '.dark-theme',
 *   ripple: true,
 * })
 * ```
 */
export function provideMeasuredDesignSystem(
  options: MdsProviderOptions = {}
): EnvironmentProviders {
  const mergedOptions = { ...DEFAULT_OPTIONS, ...options };

  return makeEnvironmentProviders([
    {
      provide: MDS_DARK_MODE_SELECTOR,
      useValue: mergedOptions.darkModeSelector
    },
    providePrimeNG({
      ripple: mergedOptions.ripple,
      theme: {
        preset: MeasuredPreset,
        options: {
          prefix: mergedOptions.prefix,
          darkModeSelector: mergedOptions.darkModeSelector,
          cssLayer: mergedOptions.cssLayer
        }
      }
    })
  ]);
}

/**
 * Provides a minimal Measured Design System configuration without CSS layer ordering.
 * Use this if you're not using Tailwind CSS or have custom CSS layer requirements.
 *
 * @param options - Optional configuration to customize the design system
 * @returns EnvironmentProviders for Angular DI
 */
export function provideMeasuredDesignSystemSimple(
  options: Omit<MdsProviderOptions, 'cssLayer'> = {}
): EnvironmentProviders {
  const mergedOptions = { ...DEFAULT_OPTIONS, ...options };

  return makeEnvironmentProviders([
    {
      provide: MDS_DARK_MODE_SELECTOR,
      useValue: mergedOptions.darkModeSelector
    },
    providePrimeNG({
      ripple: mergedOptions.ripple,
      theme: {
        preset: MeasuredPreset,
        options: {
          prefix: mergedOptions.prefix,
          darkModeSelector: mergedOptions.darkModeSelector
        }
      }
    })
  ]);
}
